import { ref } from 'vue';
import { useGlobalError } from '@/composables/useGlobalError';

const { setError, setErrorCode } = useGlobalError();

export const globalError = ref<string | null>(null);
function formatErrorMessage(msg: string | undefined | null): string {
  if (!msg) return '';
  // Remove everything before and including the first colon
  const idx = msg.indexOf(':');
  return idx !== -1 ? msg.slice(idx + 1).trim() : msg.trim();
}

export function handleAppError(error: any) {
  // Cognito/AWS error format
  console.log(error.name);
  console.log(error.code);
  if (error && (error.name || error.code)) {
    const code = error.name || error.code;
    if (code === 'UsernameExistsException') {
      globalError.value = 'User already exists. Please use a different email or log in.';
      setError(globalError.value);
      return;
    }
    // Add more Cognito error handling as needed
  }

  if (error && typeof error === 'string') {
    switch (error) {
      case 'network_error':
        globalError.value = 'Network error. Please check your internet connection.';
        setError(globalError.value);
        setErrorCode('NETWORK_ERROR');
        return;
      case 'timeout_error':
        globalError.value = 'Request timed out. Please try again later.';
        setError(globalError.value);
        setErrorCode('TIMEOUT_ERROR');
        return;
      case 'federated_authentication_failed':
        globalError.value = 'Login failed. Please try again.';
        setError(globalError.value);
        setErrorCode('FEDERATED_AUTHENTICATION_FAILED');
        return;
      default:
        globalError.value = formatErrorMessage(error) || 'An unexpected error occurred.';
        setError(globalError.value);
    }
    // If the error is a string or has a message property, format it
    globalError.value = formatErrorMessage(error) || String(error) || 'An unexpected error occurred.';
    setError(globalError.value);
  }

  // HTTP error (axios/fetch)
  if (error && error.response) {
    const status = error.response.status;
    switch (status) {
      case 401:
        globalError.value = 'Session expired. Please log in again.';
        setError(globalError.value);
        setErrorCode('UNAUTHORIZED');
        break;
      case 403:
        globalError.value = 'Access denied. You do not have permission to access this resource.';
        setError(globalError.value);
        setErrorCode('FORBIDDEN');
        break;
      case 400:
      case 422:
        globalError.value = formatErrorMessage(error.response.data?.message) || 'Invalid request. Please check your input.';
        setError(globalError.value);
        setErrorCode('INVALID_REQUEST');
        break;
      case 500:
      case 502:
        globalError.value = 'Something went wrong on our end. Please try again later.';
        setError(globalError.value);
        setErrorCode('SERVER_ERROR');
        break;
      default:
        globalError.value = formatErrorMessage(error.response.data?.message) || 'An unexpected error occurred.';
        setError(globalError.value);
        setErrorCode('UNKNOWN_ERROR');
    }
    return;
  }

  // AWS SDK error (non-HTTP)
  if (error && error.$metadata && error.$metadata.httpStatusCode) {
    const status = error.$metadata.httpStatusCode;
    switch (status) {
      case 401:
        globalError.value = 'Session expired. Please log in again.';
        setError(globalError.value);
        setErrorCode('UNAUTHORIZED');
        break;
      case 403:
        globalError.value = 'Access denied. You do not have permission to access this resource.';
        setError(globalError.value);
        setErrorCode('FORBIDDEN');
        break;
      case 400:
      case 422:
        globalError.value = formatErrorMessage(error.message) || 'Invalid request. Please check your input.';
        setError(globalError.value);
        setErrorCode('INVALID_REQUEST');
        break;
      case 500:
      case 502:
        globalError.value = 'Something went wrong on our end. Please try again later.';
        setError(globalError.value);
        setErrorCode('SERVER_ERROR');
        break;
      default:
        globalError.value = formatErrorMessage(error.message) || 'An unexpected error occurred.';
        setError(globalError.value);
        setErrorCode('UNKNOWN_ERROR');
    }
    return;
  }

  // Fallback for unknown errors
  globalError.value = formatErrorMessage(error?.message) || String(error) || 'An unexpected error occurred.';
  setError(globalError.value);
  setErrorCode('UNKNOWN_ERROR');
}
